import { ref } from 'vue'

// 用户权限状态
const userPermissions = ref(new Set<string>())

// 初始化时从localStorage加载权限
const initializePermissions = () => {
  console.log('🔍 [permission.ts] 开始初始化权限...')
  try {
    const userInfo = localStorage.getItem('userInfo')
    console.log('🔍 [permission.ts] localStorage中的userInfo:', userInfo ? '存在' : '不存在')
    
    if (userInfo) {
      const user = JSON.parse(userInfo)
      console.log('🔍 [permission.ts] 解析后的用户信息:', {
        username: user.username,
        realName: user.realName,
        permissionsCount: user.permissions ? user.permissions.length : 0,
        isSuperAdmin: user.permissions && user.permissions.includes('*:*')
      })
      
      if (user.permissions && Array.isArray(user.permissions)) {
        console.log('🔍 [permission.ts] 原始权限数组:', user.permissions)
        console.log('🔍 [permission.ts] 权限数组类型:', typeof user.permissions)
        console.log('🔍 [permission.ts] 权限数组长度:', user.permissions.length)
        
        // 检查是否有重复或无效的权限
        const uniquePermissions = [...new Set(user.permissions)]
        if (uniquePermissions.length !== user.permissions.length) {
          console.warn('⚠️ [permission.ts] 发现重复权限，原始长度:', user.permissions.length, '去重后长度:', uniquePermissions.length)
        }
        
        userPermissions.value = new Set(user.permissions)
        console.log('✅ [permission.ts] 从localStorage加载权限成功:', user.permissions)
        console.log('🔍 [permission.ts] 当前权限状态大小:', userPermissions.value.size)
        console.log('🔍 [permission.ts] Set内容验证:')
        user.permissions.forEach((perm: string) => {
          console.log(`  - "${perm}": ${userPermissions.value.has(perm)}`)
        })
      } else {
        console.log('⚠️ [permission.ts] 用户信息中没有权限数组或权限数组为空')
      }
    } else {
      console.log('⚠️ [permission.ts] localStorage中没有用户信息')
    }
  } catch (error) {
    console.error('❌ [permission.ts] 从localStorage加载权限失败:', error)
  }
}

// 强制刷新权限信息
export function refreshPermissions(): void {
  console.log('🔄 [permission.ts] 强制刷新权限信息...')
  initializePermissions()
}

// 清除权限缓存
export function clearPermissions(): void {
  console.log('🗑️ [permission.ts] 清除权限缓存...')
  userPermissions.value.clear()
  localStorage.removeItem('userInfo')
}

// 页面加载时初始化权限
initializePermissions()

// 简化的权限检查函数 - 统一使用module_code格式
export function hasPermission(moduleCode: string, operationId: string): boolean {
  const permissionKey = `${moduleCode}:${operationId}`
  const currentPermissions = Array.from(userPermissions.value)
  
  console.log(`🔍 [permission.ts] ======== 权限检查开始 ========`)
  console.log(`🔍 [permission.ts] 检查权限: ${permissionKey}`)
  console.log(`🔍 [permission.ts] 当前权限Set大小: ${userPermissions.value.size}`)
  console.log(`🔍 [permission.ts] 当前权限数组长度: ${currentPermissions.length}`)
  console.log(`🔍 [permission.ts] 权限数组前10项:`, currentPermissions.slice(0, 10))
  
  // 特别检查MOD0062和MOD0063的权限
  const mod0062Perms = currentPermissions.filter(p => p.startsWith('MOD0062:'))
  const mod0063Perms = currentPermissions.filter(p => p.startsWith('MOD0063:'))
  console.log(`🔍 [permission.ts] MOD0062权限 (${mod0062Perms.length}个):`, mod0062Perms)
  console.log(`🔍 [permission.ts] MOD0063权限 (${mod0063Perms.length}个):`, mod0063Perms)
  
  // 如果权限为空，尝试重新加载
  if (userPermissions.value.size === 0) {
    console.warn('⚠️ [permission.ts] 权限为空，尝试重新加载...')
    initializePermissions()
    // 重新获取权限后再次检查
    const reloadedPermissions = Array.from(userPermissions.value)
    console.log(`🔄 [permission.ts] 重新加载后权限数量: ${userPermissions.value.size}`)
    
    // 如果重新加载后仍然为空，返回false
    if (reloadedPermissions.length === 0) {
      console.log(`❌ [permission.ts] 重新加载后权限仍然为空，返回false: ${permissionKey}`)
      return false
    }
    
    // 如果角色配置了 *:* 权限（从数据库角色权限配置中获取），则拥有所有权限
    if (reloadedPermissions.includes('*:*')) {
      console.log(`✅ [permission.ts] 检测到 *:* 权限（从角色配置中获取），权限检查通过: ${permissionKey}`)
      return true
    }
    
    const hasReloadedPerm = reloadedPermissions.includes(permissionKey)
    console.log(`🔄 [permission.ts] 重新加载后权限检查结果: ${permissionKey} = ${hasReloadedPerm ? '✅ 有权限' : '❌ 无权限'}`)
    return hasReloadedPerm
  }
  
  // 如果角色配置了 *:* 权限（从数据库角色权限配置中获取），则拥有所有权限
  if (currentPermissions.includes('*:*')) {
    console.log(`✅ [permission.ts] 检测到 *:* 权限（从角色配置中获取），权限检查通过: ${permissionKey}`)
    return true
  }
  
  // 使用数组includes方法检查权限（更可靠）
  const hasPerm = currentPermissions.includes(permissionKey)
  console.log(`🔍 [permission.ts] Array.includes("${permissionKey}") 结果:`, hasPerm)
  
  // 额外验证：使用Set.has方法检查（用于调试）
  const hasPermSet = userPermissions.value.has(permissionKey)
  console.log(`🔍 [permission.ts] Set.has("${permissionKey}") 结果:`, hasPermSet)
  
  if (hasPerm !== hasPermSet) {
    console.error(`❌ [permission.ts] 权限检查不一致！Array.includes=${hasPerm}, Set.has=${hasPermSet}`)
    console.error(`❌ [permission.ts] 这可能是Set对象损坏，使用Array.includes作为最终结果`)
  }
  
  console.log(`🔍 [permission.ts] 最终权限检查结果: ${permissionKey} = ${hasPerm ? '✅ 有权限' : '❌ 无权限'}`)
  console.log(`🔍 [permission.ts] ======== 权限检查结束 ========`)
  
  return hasPerm // 使用Array.includes的结果作为最终结果
}

// 检查是否有模块的查看权限
export function canViewModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_001')
}

// 检查是否有模块的新增权限
export function canAddModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_002')
}

// 检查是否有模块的编辑权限
export function canEditModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_003')
}

// 检查是否有模块的删除权限
export function canDeleteModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_004')
}

// 检查是否有模块的导入权限
export function canImportModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_006')
}

// 检查是否有模块的导出权限
export function canExportModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_007')
}

// 检查是否有模块的批量操作权限
export function canBatchModule(moduleCode: string): boolean {
  return hasPermission(moduleCode, 'OP_008')
}

// 设置用户权限
export function setUserPermissions(permissions: string[]): void {
  userPermissions.value = new Set(permissions)
  
  // 同时更新localStorage中的用户信息
  try {
    const userInfo = localStorage.getItem('userInfo')
    if (userInfo) {
      const user = JSON.parse(userInfo)
      user.permissions = permissions
      localStorage.setItem('userInfo', JSON.stringify(user))
      console.log('✅ 权限已同步到localStorage:', permissions)
    }
  } catch (error) {
    console.warn('同步权限到localStorage失败:', error)
  }
}

// 获取用户权限
export function getUserPermissions(): string[] {
  return Array.from(userPermissions.value)
}

// 清除用户权限
export function clearUserPermissions(): void {
  userPermissions.value.clear()
}

// 根据权限过滤菜单
export function filterMenuByPermissions(menuData: any[]): any[] {
  if (!menuData || !Array.isArray(menuData)) {
    return []
  }

  // 获取当前权限列表用于调试
  const currentPermissions = Array.from(userPermissions.value)
  console.log('🔍 [filterMenuByPermissions] 当前权限列表:', currentPermissions.length, '个权限')
  console.log('🔍 [filterMenuByPermissions] 权限列表前20项:', currentPermissions.slice(0, 20))
  
  // 注意：不再对 *:* 做特殊处理，所有权限都从数据库角色权限配置中获取
  // 如果角色配置了 *:* 权限，则通过数据库查询返回，否则不返回
  // 这里检查是否有 *:* 权限（如果角色确实配置了这个权限）
  const hasSuperAdmin = currentPermissions.includes('*:*')
  if (hasSuperAdmin) {
    console.log('🔑 [filterMenuByPermissions] 检测到 *:* 权限（从角色配置中获取），显示所有菜单')
    return menuData
  }

  // 检查会话存档相关权限
  const archivePerms = currentPermissions.filter(p => p.includes('MOD0069'))
  console.log('🔍 [filterMenuByPermissions] 会话存档相关权限:', archivePerms)

  // 递归过滤菜单
  const filterMenu = (menus: any[]): any[] => {
    return menus.filter(menu => {
      // 先递归处理子菜单（如果有）
      if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
        const originalChildrenCount = menu.children.length
        menu.children = filterMenu(menu.children)
        // 如果子菜单全部被过滤掉，则隐藏父菜单
        if (menu.children.length === 0 && originalChildrenCount > 0) {
          console.log(`❌ [filterMenuByPermissions] 子菜单权限不足，隐藏父菜单: ${menu.meta?.title || menu.name}`)
          return false
        }
        // 如果有子菜单通过权限检查，父菜单也应该显示（即使父菜单本身有权限字段但未配置）
        if (menu.children.length > 0) {
          console.log(`✅ [filterMenuByPermissions] 父菜单有子菜单通过权限检查: ${menu.meta?.title || menu.name}, 子菜单数量: ${menu.children.length}`)
          return true
        }
      }

      // 检查菜单权限（只有在没有子菜单或子菜单全部被过滤时才检查父菜单权限）
      if (menu.meta && menu.meta.permission) {
        const hasMenuPermission = hasPermission(menu.meta.permission, 'OP_001')
        if (!hasMenuPermission) {
          console.log(`❌ [filterMenuByPermissions] 菜单权限不足: ${menu.meta.title} (${menu.meta.permission})`)
          // 如果是会话存档模块，输出更详细的调试信息
          if (menu.meta.permission.includes('MOD006')) {
            const checkPerm = `${menu.meta.permission}:OP_001`
            console.log(`🔍 [filterMenuByPermissions] 检查权限: ${checkPerm}, 是否存在: ${currentPermissions.includes(checkPerm)}`)
            console.log(`🔍 [filterMenuByPermissions] 所有MOD006相关权限:`, currentPermissions.filter(p => p.includes('MOD006')))
            console.log(`🔍 [filterMenuByPermissions] 会话存档相关权限详情:`, {
              MOD0069: currentPermissions.filter(p => p.includes('MOD0069'))
            })
          }
          return false
        } else {
          console.log(`✅ [filterMenuByPermissions] 菜单权限通过: ${menu.meta.title} (${menu.meta.permission})`)
        }
      }

      // 如果菜单没有权限字段，默认显示（可能是父菜单，权限由子菜单决定）
      return true
    })
  }

  const filteredMenus = filterMenu(menuData)
  console.log(`✅ [filterMenuByPermissions] 菜单过滤完成: ${menuData.length} -> ${filteredMenus.length}`)
  
  // 检查会话存档菜单是否在过滤后的菜单中
  const hasArchiveMenu = filteredMenus.some(menu => {
    if (menu.meta?.permission === 'MOD0069') return true
    if (menu.children) {
      return menu.children.some((child: any) => child.meta?.permission === 'MOD0069')
    }
    return false
  })
  console.log(`🔍 [filterMenuByPermissions] 会话存档菜单是否在过滤后的菜单中: ${hasArchiveMenu}`)
  
  return filteredMenus
}

// 模块编码常量（直接使用字符串，不再使用映射表）
// 基础数据
// STORES: 'MOD0021'           // 门店管理
// WAREHOUSES: 'MOD0022'       // 仓库管理
// KITCHENS: 'MOD0023'         // 后厨管理
// MATERIALS: 'MOD0024'        // 材料管理
// PRODUCT_CATEGORIES: 'MOD0025' // 产品分类
// MATERIAL_CATEGORIES: 'MOD0026' // 材料分类
// RECIPES: 'MOD0027'          // 配方管理
// PRODUCT_SKUS: 'MOD0028'     // 产品SKU
// PRODUCT_BOMS: 'MOD0029'     // 产品BOM
// SUPPLIERS: 'MOD002A'        // 供应商管理
// LOSS_REASON: 'MOD002B'      // 货损原因

// 库存管理
// PURCHASE_RECORD: 'MOD0031'  // 材料采购记录
// MATERIAL_INVENTORY: 'MOD0032' // 材料库存管理
// PRODUCT_INVENTORY: 'MOD0033' // 产品库存管理
// INVENTORY_TRANSACTIONS: 'MOD0034' // 库存流水记录
// INVENTORY_ADJUSTMENTS: 'MOD0035' // 库存调整管理

// 产销管理
// PRODUCTION_RECORDS: 'MOD0041' // 生产记录管理
// SALES_RECORD: 'MOD0042'     // 销售记录管理
// LOSS_RECORDS: 'MOD0043'     // 报损记录管理

// 统计报表
// INVENTORY_OVERVIEW: 'MOD0051' // 库存概览
// DAILY_RECONCILIATION: 'MOD0052' // 日对账报表
// PERSONAL_DAILY_RECONCILIATION: 'MOD0053' // 个人日对账

// 系统管理
// CODE_RULES: 'MOD0061'       // 编码规则
// STAFF_MANAGEMENT: 'MOD0062' // 人员管理
// ROLE_MANAGEMENT: 'MOD0063'  // 角色管理
// PERMISSION_MANAGEMENT: 'MOD0064' // 权限管理
// MODULE_MANAGEMENT: 'MOD0065' // 模块管理
// SYSTEM_BACKUP: 'MOD0066'    // 系统备份
// LLM_MODEL_CONFIG: 'MOD0067' // 大模型配置
// OPERATION_MANAGEMENT: 'MOD0066' // 操作管理

// 模块ID常量导出
export const MODULE_IDS = {
  LLM_MODEL_CONFIG: 'MOD0067' // 大模型配置
}

// 操作ID映射
export const OPERATION_IDS = {
  VIEW: 'OP_001',      // 查看
  ADD: 'OP_002',       // 新增
  EDIT: 'OP_003',      // 编辑
  DELETE: 'OP_004',    // 删除
  WRITE: 'OP_005',     // 写入
  IMPORT: 'OP_006',    // 导入
  EXPORT: 'OP_007',    // 导出
  BATCH: 'OP_008',     // 批量操作
  APPROVE: 'OP_009',   // 审批
} 